/**
 * Admin lexicon API client — export every translatable string, import them back.
 */

import type { LexiconRow, LexiconImportRow } from './lexiconSheet';

const API_BASE = window.location.hostname === 'localhost' ? 'http://chriscme/api' : '/api';

export interface LexiconExport {
  locale: string;
  rows: LexiconRow[];
  counts: { total: number; translated: number; ui: number; db: number };
}

export interface LexiconImportResult {
  locale: string;
  dryRun: boolean;
  /** Rows that would be (or were) written. */
  applied: number;
  /** Rows whose translation was blanked, reverting them to English. */
  cleared: number;
  /** Rows already holding this exact value — the bulk of a re-import. */
  unchanged: number;
  /**
   * Rows placed by their English text rather than their reference — the file
   * came from another environment, or content was renumbered since the export.
   */
  remapped: number;
  skipped: { ref: string; reason: string }[];
}

async function handle<T>(res: Response): Promise<T> {
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error((data as { error?: string })?.error || `Request failed (${res.status})`);
  return data as T;
}

/** Coverage for one language, without the row payload. */
export interface LexiconCoverage {
  code: string;
  englishName: string;
  total: number;
  translated: number;
  missing: number;
  /** A capped sample of what is still untranslated — a nudge, not a worklist. */
  missingSample: { ref: string; context: string; english: string }[];
}

/**
 * Coverage across every active language.
 *
 * The point of this call is that a missing translation is otherwise SILENT: the
 * fallback chain quietly serves English and nothing anywhere says so. This is
 * the one place the gap is visible without exporting a spreadsheet.
 */
export async function fetchLexiconCoverage(): Promise<LexiconCoverage[]> {
  const data = await handle<{ locales: LexiconCoverage[] }>(
    await fetch(`${API_BASE}/admin-lexicon.php?summary=1`, { credentials: 'include' }),
  );
  return data.locales;
}

/** The complete lexicon for one locale, with current translations pre-filled. */
export async function fetchLexicon(locale: string): Promise<LexiconExport> {
  return handle<LexiconExport>(
    await fetch(`${API_BASE}/admin-lexicon.php?locale=${encodeURIComponent(locale)}`, {
      credentials: 'include',
    }),
  );
}

/**
 * Submit a parsed lexicon.
 *
 * Call with dryRun first: the counts come back without anything being written,
 * so the admin sees "412 changes, 3 unrecognised" and decides. That preview →
 * confirm shape matches the product importer, and matters more here — an import
 * touches every string in the product at once.
 */
export async function submitLexicon(
  locale: string,
  rows: LexiconImportRow[],
  dryRun: boolean,
): Promise<LexiconImportResult> {
  return handle<LexiconImportResult>(
    await fetch(`${API_BASE}/admin-lexicon.php`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ locale, rows, dryRun }),
    }),
  );
}
